import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Mail, Save, RotateCcw, Eye, Edit3, ChevronRight, Power } from "lucide-react";
import { Switch } from "../ui/switch";
import { sanitizeHtml } from "../../lib/safeMarkdown";

export default function EmailTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [draft, setDraft] = useState({ subject: "", body_html: "", enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-templates`);
      setTemplates(res.data.templates || []);
      setPlaceholders(res.data.placeholders || []);
      if (!activeKey && res.data.templates?.length) {
        setActiveKey(res.data.templates[0].key);
      }
    } catch (e) {
      console.error("Failed to load templates", e);
      toast.error("Failed to load email templates");
    } finally { setLoading(false); }
  }, [activeKey]);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(() => templates.find(t => t.key === activeKey) || null, [templates, activeKey]);

  useEffect(() => {
    if (active) {
      setDraft({ subject: active.subject, body_html: active.body_html, enabled: active.enabled !== false });
      setShowPreview(false);
    }
  }, [active]);

  const dirty = active && (
    draft.subject !== active.subject ||
    draft.body_html !== active.body_html ||
    draft.enabled !== (active.enabled !== false)
  );

  const save = async () => {
    if (!activeKey) return;
    if (!draft.subject.trim() || !draft.body_html.trim()) { toast.error("Subject and body are required"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/email-templates/${activeKey}`, draft);
      toast.success("Template saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!activeKey) return;
    if (!window.confirm("Reset this template to its default content?")) return;
    setResetting(true);
    try {
      await axios.post(`${API}/admin/email-templates/${activeKey}/reset`);
      toast.success("Template reset to default");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset");
    } finally { setResetting(false); }
  };

  const insertPlaceholder = (ph) => {
    const token = `{${ph}}`;
    setDraft(p => ({ ...p, body_html: p.body_html + token }));
  };

  const previewHtml = useMemo(() => {
    // Render with example data so the operator sees a realistic preview
    const sample = {
      customer_name: "Mario Rossi", customer_email: "mario@example.com", customer_phone: "+39 333 1234567",
      origin: "Venice Airport", destination: "Hotel Danieli", pickup_date: "2026-05-12", pickup_time: "10:30",
      total_price: "120.00", quote_id: "quote_abc123", dashboard_link: "https://taxinearme.it/dashboard",
      driver_name: "Luca Bianchi", driver_phone: "+39 333 7654321", driver_email: "luca@taxinearme.it",
      custom_message: "We have a small change to your itinerary as requested.", actor_name: "operator",
      first_name: "Mario",
    };
    let body = draft.body_html;
    Object.entries(sample).forEach(([k, v]) => { body = body.split(`{${k}}`).join(v); });
    return { __html: sanitizeHtml(body) };
  }, [draft.body_html]);

  if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>;

  return (
    <div data-testid="email-templates-tab" className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
      {/* Sidebar */}
      <aside className="bg-[#0F0F0F] border border-white/5 rounded-sm p-3 space-y-1 max-h-[80vh] overflow-y-auto">
        <h3 className="text-white/40 text-[10px] uppercase tracking-wide px-2 py-2 flex items-center gap-2">
          <Mail className="w-3 h-3" /> Templates
        </h3>
        {templates.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveKey(t.key)}
            className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-sm text-xs transition-colors ${activeKey === t.key ? "bg-[#C5A059]/10 text-[#C5A059] border border-[#C5A059]/30" : "text-white/60 hover:text-white hover:bg-white/5"}`}
            data-testid={`template-tab-${t.key}`}
          >
            <span className="truncate pr-2 flex items-center gap-2">
              {t.enabled === false && <Power className="w-3 h-3 text-amber-400 flex-shrink-0" />}
              {t.label}
            </span>
            <ChevronRight className="w-3 h-3 flex-shrink-0" />
          </button>
        ))}
      </aside>

      {/* Editor */}
      <section className="space-y-4">
        {!active ? (
          <p className="text-white/40">Select a template to edit.</p>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-white">{active.label}</h2>
                <p className="text-white/40 text-xs font-mono mt-1">key: {active.key}{active.updated_at ? ` \u00b7 last updated ${active.updated_at.slice(0,16).replace("T"," ")}` : " \u00b7 default content"}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => setShowPreview(p => !p)} className="text-white/60" data-testid="template-preview-toggle">
                  {showPreview ? <Edit3 className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                  {showPreview ? "Edit" : "Preview"}
                </Button>
                <Button size="sm" variant="ghost" onClick={reset} disabled={resetting} className="text-amber-400 hover:bg-amber-500/10" data-testid="template-reset-btn">
                  <RotateCcw className="w-4 h-4 mr-1" /> Reset
                </Button>
                <Button size="sm" onClick={save} disabled={!dirty || saving} className="btn-gold" data-testid="template-save-btn">
                  <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>

            {/* Auto-send toggle */}
            <div className={`bg-[#1A1A1A] border rounded-sm p-3 flex items-start gap-3 ${draft.enabled ? "border-emerald-500/20" : "border-amber-500/30"}`} data-testid="template-enabled-card">
              <Switch
                checked={draft.enabled}
                onCheckedChange={(c) => setDraft(p => ({ ...p, enabled: c }))}
                data-testid="template-enabled-switch"
              />
              <div className="flex-1">
                <p className={`text-sm font-semibold flex items-center gap-1 ${draft.enabled ? "text-emerald-400" : "text-amber-400"}`}>
                  <Power className="w-3 h-3" /> Automatic send {draft.enabled ? "ENABLED" : "DISABLED"}
                </p>
                <p className="text-white/40 text-xs">
                  {draft.enabled
                    ? "This email is sent automatically when its trigger event fires."
                    : "When OFF, the system skips this email silently. Useful while you tune the wording."}
                </p>
              </div>
            </div>

            {/* Placeholders */}
            <div className="bg-[#1A1A1A] border border-white/5 rounded-sm p-3" data-testid="template-placeholders">
              <p className="text-white/40 text-[10px] uppercase mb-2">Available placeholders (click to insert)</p>
              <div className="flex flex-wrap gap-2">
                {placeholders.map(ph => (
                  <button
                    key={ph}
                    type="button"
                    onClick={() => insertPlaceholder(ph)}
                    className="text-[11px] font-mono bg-[#C5A059]/10 hover:bg-[#C5A059]/20 text-[#C5A059] border border-[#C5A059]/20 px-2 py-1 rounded-sm transition-colors"
                    data-testid={`placeholder-${ph}`}
                  >
                    {`{${ph}}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="text-white/60 text-xs mb-1 block">Subject</label>
              <Input
                value={draft.subject}
                onChange={(e) => setDraft(p => ({ ...p, subject: e.target.value }))}
                className="input-dark"
                data-testid="template-subject"
              />
            </div>

            {/* Body */}
            <div>
              <label className="text-white/60 text-xs mb-1 block">Body (HTML allowed)</label>
              {showPreview ? (
                <div className="bg-white text-black p-4 rounded-sm border border-white/10 min-h-[300px]" data-testid="template-preview-pane" dangerouslySetInnerHTML={previewHtml} />
              ) : (
                <textarea
                  value={draft.body_html}
                  onChange={(e) => setDraft(p => ({ ...p, body_html: e.target.value }))}
                  className="w-full min-h-[300px] bg-[#1A1A1A] border border-[#333] rounded-sm p-3 text-white/80 text-xs font-mono resize-y focus:border-[#C5A059]/50 focus:outline-none"
                  data-testid="template-body"
                />
              )}
              <p className="text-white/30 text-[10px] mt-1 italic">Preview uses sample data for placeholders. Real emails are sanitized via DOMPurify before sending.</p>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
